/**
 * First-touch attribution captured in the browser.
 *
 * We store the first page and referrer of the visit in sessionStorage, so that a
 * visitor who lands on a guide from Google and converts three pages later is
 * still credited to the guide and the search term that brought them in.
 *
 * No personal data is captured here — only page paths, the referring host and
 * campaign/search terms already present in the URL.
 */

export type Attribution = {
  sourcePage: string;
  landingPage?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  keyword?: string;
  gclid?: string;
};

const KEY = "csl_attribution_v1";

type StoredAttribution = Omit<Attribution, "sourcePage">;

const SEARCH_HOSTS: { match: RegExp; label: string }[] = [
  { match: /(^|\.)google\./i, label: "google" },
  { match: /(^|\.)bing\./i, label: "bing" },
  { match: /duckduckgo\./i, label: "duckduckgo" },
  { match: /(^|\.)yahoo\./i, label: "yahoo" },
  { match: /(^|\.)ecosia\./i, label: "ecosia" },
  { match: /(chatgpt|openai)\.com$/i, label: "chatgpt" },
  { match: /perplexity\.ai$/i, label: "perplexity" },
  { match: /(^|\.)gemini\.google\./i, label: "gemini" },
  { match: /claude\.ai$/i, label: "claude" },
  { match: /copilot\.microsoft\./i, label: "copilot" },
];

function clean(value: string | null | undefined, max = 160): string | undefined {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, max);
}

function labelForReferrer(referrer: string): string | undefined {
  try {
    const host = new URL(referrer).hostname;
    const known = SEARCH_HOSTS.find((entry) => entry.match.test(host));
    return known ? known.label : host.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

function keywordFromReferrer(referrer: string): string | undefined {
  try {
    const url = new URL(referrer);
    for (const param of ["q", "query", "p", "search"]) {
      const value = clean(url.searchParams.get(param), 120);
      if (value) return value.toLowerCase();
    }
  } catch {
    /* ignore malformed referrers */
  }
  return undefined;
}

function captureFirstTouch(): StoredAttribution {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer && !document.referrer.includes(window.location.host)
    ? document.referrer
    : "";

  const gclid = clean(params.get("gclid"), 200);
  const adClick = gclid || clean(params.get("gbraid"), 200) || clean(params.get("wbraid"), 200);
  const utmSource = clean(params.get("utm_source"), 80) ?? (adClick ? "google" : referrer ? labelForReferrer(referrer) : "direct");
  const utmMedium =
    clean(params.get("utm_medium"), 80) ??
    (adClick ? "cpc" : referrer ? (SEARCH_HOSTS.some((e) => e.match.test(referrer)) ? "organic" : "referral") : "direct");

  const landingPage = clean(window.location.pathname, 200);
  const utmCampaign = clean(params.get("utm_campaign"), 120);

  const stored: StoredAttribution = {
    ...(gclid ? { gclid } : {}),
    ...(landingPage ? { landingPage } : {}),
    ...(referrer ? { referrer: referrer.trim().slice(0, 200) } : {}),
    ...(utmSource ? { utmSource } : {}),
    ...(utmMedium ? { utmMedium } : {}),
    ...(utmCampaign ? { utmCampaign } : {}),
  };

  const keyword =
    clean(params.get("utm_term"), 120)?.toLowerCase() ??
    clean(params.get("kw"), 120)?.toLowerCase() ??
    (referrer ? keywordFromReferrer(referrer) : undefined);
  if (keyword) stored.keyword = keyword;

  return stored;
}

/** Records first-touch data once per browser session. Safe to call on every mount. */
export function recordVisit(): void {
  if (typeof window === "undefined") return;
  if (["/enquiry-tracking", "/analytics"].includes(window.location.pathname)) return;
  try {
    if (window.sessionStorage.getItem(KEY)) return;
    window.sessionStorage.setItem(KEY, JSON.stringify(captureFirstTouch()));
  } catch {
    /* private browsing / storage disabled */
  }
}

/** Reads first-touch data and pairs it with the page the form was submitted from. */
export function getAttribution(sourcePage: string): Attribution {
  if (typeof window === "undefined") return { sourcePage };
  let stored: StoredAttribution = {};
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw) stored = JSON.parse(raw) as StoredAttribution;
    else {
      stored = captureFirstTouch();
      window.sessionStorage.setItem(KEY, JSON.stringify(stored));
    }
  } catch {
    /* ignore */
  }
  return { sourcePage, ...stored };
}
